import { Card } from '@/components/ui';
import { Alert } from '@/components/ui/alert';
import { Field } from '@/components/ui/field';
import { useRequestPasswordResetAction } from '@/hooks/actions/use-request-password-reset-action';
import {
  Box,
  Button,
  Container,
  Fieldset,
  Heading,
  Input,
  Link,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Logo } from '@fluenceur/ui';
import { zodResolver } from '@hookform/resolvers/zod';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ArrowLeft } from 'iconoir-react';

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const requestPasswordResetMutation = useRequestPasswordResetAction();

  // Schema definition with translated messages
  const forgotPasswordSchema = z.object({
    email: z
      .string()
      .min(1, t('auth.validation.emailRequired'))
      .email(t('auth.validation.emailInvalid')),
  });

  type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      const response = await requestPasswordResetMutation.mutateAsync(data.email);
      // Use translation key if code is provided, otherwise use message
      const message = response.code
        ? t(`messages.${response.code}`)
        : response.message || t('auth.forgotPassword.emailSent');
      setSuccessMessage(message);
    } catch (error: any) {
      const message = error?.response?.data?.message || t('auth.errors.unexpectedError');
      setErrorMessage(message);
    }
  };

  return (
    <Box
      minHeight="100vh"
      bg="softCream"
      width="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
      py={{ base: 12, md: 16 }}
    >
      <VStack gap={6} align="center" width="full">
        <Logo h="28px" />
        <Container maxW="500px" width="full">
          {/* Forgot Password Form Card */}
          <Card.Root bg="babyPowder">
            <Card.Body>
              <Stack as="form" onSubmit={handleSubmit(onSubmit)} gap={4} width="full">
                {/* Title and Description */}
                <VStack gap={{ base: 2, md: 3 }} mb={2}>
                  <Heading as="h1" size="xl" textAlign="center">
                    {t('auth.forgotPassword.title')}
                  </Heading>
                  <Text fontSize="md" textAlign="center">
                    {t('auth.forgotPassword.description')}
                  </Text>
                </VStack>

                {/* Success Alert */}
                {successMessage && (
                  <Alert status="success" title={t('common.success')} textAlign="center">
                    {successMessage}
                  </Alert>
                )}

                {/* Error Alert */}
                {errorMessage && (
                  <Alert status="error" title={t('common.error')} textAlign="center">
                    {errorMessage}
                  </Alert>
                )}

                {/* Form Fields */}
                <Fieldset.Root size="lg" width="100%" colorPalette="gray">
                  <Fieldset.Content>
                    <Field
                      label={t('auth.emailAddress')}
                      errorText={errors.email?.message}
                      required
                    >
                      <Input
                        {...register('email')}
                        type="email"
                        placeholder={t('auth.emailPlaceholder')}
                        variant="outline"
                        autoComplete="email"
                        size="lg"
                      />
                    </Field>
                  </Fieldset.Content>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    colorPalette="gray"
                    size="lg"
                    width="full"
                    loading={isSubmitting}
                    loadingText={t('auth.forgotPassword.sending')}
                    h={12}
                    mt={{ base: 4, md: 6 }}
                  >
                    {t('auth.forgotPassword.sendResetLink')}
                  </Button>
                </Fieldset.Root>

                {/* Back to login link */}
                <Button variant="ghost" size="sm" asChild mt={2}>
                  <Link href="/" _hover={{ textDecoration: 'none' }}>
                    <ArrowLeft />
                    {t('auth.forgotPassword.backToLogin')}
                  </Link>
                </Button>
              </Stack>
            </Card.Body>
          </Card.Root>
        </Container>
        <Link href="mailto:contact@fluenceur.com" fontSize="sm">
          {t('auth.needHelp')}
        </Link>
      </VStack>
    </Box>
  );
};

export default ForgotPasswordPage;
